// An ID describes an installation; only the separate, per-user secret proves
// possession after the server has confirmed it. Never fall back to Math.random.
const ephemeralDeviceTokens = new Map();

export function getDeviceToken(userId) {
  if (!userId) return null;
  const key = `node_device_token_${userId}`;
  let token;
  try { token = localStorage.getItem(key); } catch (_) {}
  if (/^[a-f0-9]{64}$/.test(token || '')) return token;
  if (ephemeralDeviceTokens.has(userId)) return ephemeralDeviceTokens.get(userId);
  const cryptoApi = typeof window !== 'undefined' ? window.crypto : null;
  if (!cryptoApi?.getRandomValues) return null;
  token = Array.from(cryptoApi.getRandomValues(new Uint8Array(32)), byte => byte.toString(16).padStart(2, '0')).join('');
  ephemeralDeviceTokens.set(userId, token);
  try { localStorage.setItem(key, token); } catch (_) {}
  return token;
}

// Generate or retrieve a persistent device ID
export function getDeviceId() {
  const DEVICE_ID_KEY = 'node_device_id';
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);

  if (!deviceId) {
    // Generate a unique device ID (UUID v4)
    deviceId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }
    );
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }

  return deviceId;
}

// Parse device information from User-Agent
export function getDeviceInfo() {
  const ua = navigator.userAgent;

  // Detect device type
  const isMobile =
    /iPhone|iPad|iPod|Android|webOS|BlackBerry|Windows Phone/i.test(ua);
  const isTablet = /iPad|Android(?!.*Mobile)|Tablet/i.test(ua);

  // Extract OS information
  let osName = 'Unknown';
  let osVersion = '';

  if (/Mac OS X/.test(ua) && !/iPhone|iPad|iPod/.test(ua)) {
    osName = 'macOS';
    const match = ua.match(/Mac OS X ([\d._]+)/);
    if (match) osVersion = match[1].replace(/_/g, '.');
  } else if (/Windows NT/.test(ua)) {
    osName = 'Windows';
    const match = ua.match(/Windows NT ([\d.]+)/);
    if (match) {
      const version = match[1];
      osVersion = version === '10.0' ? '10/11' : version;
    }
  } else if (/iPhone|iPad|iPod/.test(ua)) {
    osName = /iPad/.test(ua) ? 'iPadOS' : 'iOS';
    const match = ua.match(/OS ([\d_]+)/);
    if (match) osVersion = match[1].replace(/_/g, '.');
  } else if (/Android/.test(ua)) {
    osName = 'Android';
    const match = ua.match(/Android ([\d.]+)/);
    if (match) osVersion = match[1];
  } else if (/Linux/.test(ua)) {
    osName = 'Linux';
  }

  // Extract device model (mainly for mobile)
  let deviceModel = '';
  if (/iPhone/.test(ua)) {
    deviceModel = 'iPhone';
  } else if (/iPad/.test(ua)) {
    deviceModel = 'iPad';
  } else if (/Android/.test(ua)) {
    const match = ua.match(/Android.*; ([^;)]+)\)/);
    if (match) deviceModel = match[1].trim();
  }

  // Extract browser information
  let browserName = 'Unknown';
  let browserVersion = '';

  if (/Edg\//.test(ua)) {
    browserName = 'Edge';
    const match = ua.match(/Edg\/([\d.]+)/);
    if (match) browserVersion = match[1];
  } else if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) {
    browserName = 'Chrome';
    const match = ua.match(/Chrome\/([\d.]+)/);
    if (match) browserVersion = match[1];
  } else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) {
    browserName = 'Safari';
    const match = ua.match(/Version\/([\d.]+)/);
    if (match) browserVersion = match[1];
  } else if (/Firefox\//.test(ua)) {
    browserName = 'Firefox';
    const match = ua.match(/Firefox\/([\d.]+)/);
    if (match) browserVersion = match[1];
  }

  // Build device name
  let deviceName = '';
  if (deviceModel) {
    deviceName = deviceModel;
  } else if (isTablet) {
    deviceName = 'Tablet';
  } else if (isMobile) {
    deviceName = 'Mobile';
  } else {
    deviceName = 'Desktop';
  }

  return {
    deviceName,
    deviceType: isTablet ? 'tablet' : isMobile ? 'mobile' : 'desktop',
    osName,
    osVersion,
    osFull: osVersion ? `${osName} ${osVersion}` : osName,
    browserName,
    browserVersion,
    browserFull: browserVersion
      ? `${browserName} ${browserVersion}`
      : browserName,
  };
}

export const IOS_APP_STORE_URL =
  'https://apps.apple.com/app/bos-node/id6744272413';
export const ANDROID_PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.nodecanada.bos';

const PUBLIC_MOBILE_BROWSER_PATHS = [
  '/mcp',
  '/auth-callback',
  '/validate',
  '/oauth',
  '/aura/board',
  '/redirect',
  '/booking/manage',
  '/booking-addon',
  '/build',
  '/presentation',
  '/demo',
  '/diagnostic-ops',
  '/ecosystem',
  '/onboarding',
  '/admin/portal/invitation',
  '/public-approval',
  '/avatar-upload',
  '/welcome',
  '/feedback',
  '/doc',
  '/find',
  '/page',
  '/docs',
];

function resolveUserAgent(ua) {
  if (ua != null) return ua;
  if (typeof navigator === 'undefined') return '';
  return navigator.userAgent || '';
}

function resolveMaxTouchPoints(maxTouchPoints) {
  if (maxTouchPoints != null) return maxTouchPoints;
  if (typeof navigator === 'undefined') return 0;
  return navigator.maxTouchPoints || 0;
}

export function isIpad(ua, maxTouchPoints) {
  const resolvedUa = resolveUserAgent(ua);
  const resolvedTouch = resolveMaxTouchPoints(maxTouchPoints);
  if (/iPad/i.test(resolvedUa)) return true;
  // iPadOS 13+ can report as Macintosh with touch.
  return /Macintosh/i.test(resolvedUa) && resolvedTouch > 1;
}

export function isSmartphone(ua, maxTouchPoints) {
  const resolvedUa = resolveUserAgent(ua);
  if (!resolvedUa) return false;
  if (isIpad(resolvedUa, maxTouchPoints)) return false;
  if (/Tablet|PlayBook|Silk/i.test(resolvedUa)) return false;
  if (/Android/i.test(resolvedUa) && !/Mobile/i.test(resolvedUa)) return false;

  if (/iPhone|iPod/i.test(resolvedUa)) return true;
  if (/Android/i.test(resolvedUa) && /Mobile/i.test(resolvedUa)) return true;
  if (/Windows Phone|IEMobile|BlackBerry|webOS|Opera Mini/i.test(resolvedUa)) {
    return true;
  }
  return false;
}

export function getSmartphoneOs(ua) {
  const resolvedUa = resolveUserAgent(ua);
  if (/iPhone|iPod/i.test(resolvedUa)) return 'ios';
  if (/Android/i.test(resolvedUa)) return 'android';
  return 'other';
}

const MOBILE_APP_PROMPT_HOSTS = [
  'osiris.usenode.com',
  'admin.usenode.com',
];

function resolveHostname(hostname) {
  if (hostname != null) return hostname;
  if (typeof window === 'undefined') return '';
  return window.location.hostname || '';
}

export function isMobileAppPromptHost(hostname) {
  const host = String(resolveHostname(hostname)).toLowerCase();
  return MOBILE_APP_PROMPT_HOSTS.includes(host);
}

export function isPublicMobileBrowserPath(pathname = '') {
  const path = pathname.split('?')[0];
  return PUBLIC_MOBILE_BROWSER_PATHS.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  );
}

export function isAddonBookingRoute(pathname = '') {
  const path = pathname.split('?')[0];
  return path === '/booking-addon' || path.startsWith('/booking-addon/');
}

export function shouldPromptMobileAppDownload(
  pathname,
  ua,
  maxTouchPoints,
  hostname
) {
  return (
    isMobileAppPromptHost(hostname) &&
    isSmartphone(ua, maxTouchPoints) &&
    !isPublicMobileBrowserPath(pathname)
  );
}

// Get all device headers ready to use in API calls
export function getDeviceHeaders(userId) {
  const deviceInfo = getDeviceInfo();
  const token = getDeviceToken(userId);

  return {
    'Device-ID': getDeviceId(),
    ...(token && { 'Device-Token': token }),
    'Device-Name': deviceInfo.deviceName,
    'Device-Type': deviceInfo.deviceType,
    'Device-OS': deviceInfo.osFull,
    'Device-Browser': deviceInfo.browserFull,
    'Device-Platform': navigator.platform,
    'Device-Screen': `${window.screen.width}x${window.screen.height}`,
    'Device-Language': navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}
